import { Link, Navigate, useLocation } from "react-router-dom";
import Reveal from "@/components/Reveal";
import WordReveal from "@/components/WordReveal";
import { useSeo, SITE_URL } from "@/lib/seo";
import { breadcrumb } from "@/lib/structuredData";
import { useStartProject } from "@/lib/startProjectContext";
import { UK_CATEGORIES } from "@/data/ukCategories";
import { NL_CATEGORIES } from "@/data/nlCategories";
import { DE_CATEGORIES } from "@/data/deCategories";
import { FR_CATEGORIES } from "@/data/frCategories";
import { SE_CATEGORIES } from "@/data/seCategories";
import { FI_CATEGORIES } from "@/data/fiCategories";
import { SPRINT_C_CATEGORIES } from "@/data/sprintCCategories";
import { useSeoPageDraft } from "@/lib/useSeoPageDraft";
import { stripLocale } from "@/lib/i18n";

// Country registry — maps URL prefix → country metadata for the shared
// RegionCategory template. Adding a new country here + a matching data
// export is all that's needed to onboard a new Phase 1-style category
// silo.
const COUNTRIES = {
  "/regions/uk/": { countrySlug: "/regions/uk", countryName: "United Kingdom" },
  "/regions/netherlands/": { countrySlug: "/regions/netherlands", countryName: "Netherlands" },
  "/regions/germany/": { countrySlug: "/regions/germany", countryName: "Germany" },
  "/regions/france/": { countrySlug: "/regions/france", countryName: "France" },
  "/regions/sweden/": { countrySlug: "/regions/sweden", countryName: "Sweden" },
  "/regions/finland/": { countrySlug: "/regions/finland", countryName: "Finland" },
  // Sprint C · Tier-2 markets
  "/regions/canada/": { countrySlug: "/regions/canada", countryName: "Canada" },
  "/regions/spain/": { countrySlug: "/regions/spain", countryName: "Spain" },
  "/regions/italy/": { countrySlug: "/regions/italy", countryName: "Italy" },
  "/regions/poland/": { countrySlug: "/regions/poland", countryName: "Poland" },
  "/usa/": { countrySlug: "/usa", countryName: "United States" },
};

const ALL_CATEGORIES = [
  ...UK_CATEGORIES,
  ...NL_CATEGORIES,
  ...DE_CATEGORIES,
  ...FR_CATEGORIES,
  ...SE_CATEGORIES,
  ...FI_CATEGORIES,
  ...SPRINT_C_CATEGORIES,
];

function resolveCountry(pathname) {
  for (const prefix of Object.keys(COUNTRIES)) {
    if (pathname.startsWith(prefix)) return COUNTRIES[prefix];
  }
  return COUNTRIES["/regions/uk/"];
}

/**
 * Shared template for UK-scoped category-and-service landing pages
 * (Phase 1: /regions/uk/furniture-sourcing, /home-decor-sourcing,
 *  /kitchenware-sourcing, /lighting-sourcing, /home-textile-sourcing,
 *  /private-label-sourcing, /india-buying-agent).
 *
 * Data-driven from ukCategories.js. Emits Service + FAQPage +
 * BreadcrumbList JSON-LD (Organization + WebSite come from the
 * global block in public/index.html — no duplication).
 */
export const RegionCategory = () => {
  const location = useLocation();
  const canonicalPath = stripLocale(location.pathname);
  const page = ALL_CATEGORIES.find((p) => p.slug === canonicalPath);
  const country = resolveCountry(canonicalPath);
  const { open } = useStartProject();
  const draft = useSeoPageDraft(page ? page.slug : null);

  useSeo({
    title: page ? page.title : `${country.countryName} category programme`,
    description: page ? page.metaDescription : undefined,
    path: page ? page.slug : country.countrySlug,
    type: "article",
    image: draft.heroUrl || undefined,
    jsonLd: page
      ? {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Service",
              "@id": `${SITE_URL}${page.slug}#service`,
              name: page.h1,
              description: page.metaDescription,
              provider: { "@id": `${SITE_URL}#organization` },
              areaServed: { "@type": "Country", name: country.countryName },
              serviceType: `India sourcing · ${page.category} · ${country.countryName} buyers`,
              url: `${SITE_URL}${page.slug}`,
              isPartOf: {
                "@type": "WebPage",
                "@id": `${SITE_URL}${country.countrySlug}`,
                name: `India Sourcing · ${country.countryName}`,
              },
            },
            {
              "@type": "FAQPage",
              "@id": `${SITE_URL}${page.slug}#faq`,
              mainEntity: (page.faqs || []).map(([q, a]) => ({
                "@type": "Question",
                name: q,
                acceptedAnswer: { "@type": "Answer", text: a },
              })),
            },
            breadcrumb([
              { name: "Home", path: "/" },
              { name: "Regions", path: "/regions" },
              { name: country.countryName, path: country.countrySlug },
              { name: page.category, path: page.slug },
            ]),
          ],
        }
      : null,
    jsonLdId: "region-category-jsonld",
  });

  if (!page) return <Navigate to={country.countrySlug} replace />;

  // Sibling categories within the SAME country only
  const siblings = ALL_CATEGORIES.filter(
    (c) => c.slug !== page.slug && resolveCountry(c.slug).countrySlug === country.countrySlug
  );

  return (
    <article data-testid="region-category-page">
      {draft.heroUrl && (
        <figure
          data-testid="region-category-hero-image"
          style={{
            margin: 0,
            width: "100%",
            maxHeight: "clamp(240px, 36vw, 440px)",
            overflow: "hidden",
            background: "var(--ink)",
          }}
        >
          <img
            src={draft.heroUrl}
            alt={draft.heroAlt || page.h1}
            loading="eager"
            fetchpriority="high"
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
          {draft.heroCaption && (
            <figcaption
              className="mono"
              style={{
                fontSize: ".72rem",
                letterSpacing: ".06em",
                color: "var(--ink-soft)",
                padding: ".5rem 1rem",
                background: "var(--paper-raised)",
                borderBottom: "1px solid var(--line)",
              }}
            >
              {draft.heroCaption}
            </figcaption>
          )}
        </figure>
      )}
      <header className="wrap" style={{ paddingBlock: "calc(74px + var(--section)) 2rem" }}>
        <Reveal as="nav" aria-label="Breadcrumbs" style={{ marginBottom: "1.2rem" }}>
          <p
            className="mono"
            style={{
              fontSize: ".72rem",
              color: "var(--brass-deep)",
              letterSpacing: ".14em",
              textTransform: "uppercase",
            }}
          >
            <Link to="/" style={{ color: "inherit" }} data-testid="region-category-crumb-home">
              Home
            </Link>{" "}
            ·{" "}
            <Link
              to="/regions"
              style={{ color: "inherit" }}
              data-testid="region-category-crumb-regions"
            >
              Regions
            </Link>{" "}
            ·{" "}
            <Link
              to={country.countrySlug}
              style={{ color: "inherit" }}
              data-testid="region-category-crumb-country"
            >
              {country.countryName}
            </Link>{" "}
            · {page.category}
          </p>
        </Reveal>
        <Reveal>
          <p className="eyebrow" data-testid="region-category-eyebrow">
            {page.eyebrow}
          </p>
        </Reveal>
        <WordReveal
          as="h1"
          text={page.h1}
          className="display tp-title"
          stagger={70}
          testId="region-category-h1"
        />
        <Reveal
          as="p"
          delay={200}
          style={{
            maxWidth: "72ch",
            color: "var(--ink-soft)",
            fontSize: "1.1rem",
            marginTop: "1.4rem",
            lineHeight: 1.65,
          }}
          data-testid="region-category-lede"
        >
          {page.lede}
        </Reveal>
        <Reveal
          as="div"
          delay={280}
          style={{ display: "flex", flexWrap: "wrap", gap: ".8rem", marginTop: "2rem" }}
        >
          <button
            type="button"
            className="btn btn-fill btn-lg btn-glow"
            onClick={open}
            data-testid="region-category-cta-primary"
          >
            Start Your Project →
          </button>
          <Link
            to={country.countrySlug}
            className="btn btn-ghost"
            data-testid="region-category-cta-country"
          >
            Back to {country.countryName}
          </Link>
        </Reveal>
      </header>

      {/* KEY FACTS strip */}
      <section
        className="wrap"
        data-testid="region-category-facts"
        style={{
          paddingBlock: "clamp(2rem, 4vw, 3.4rem)",
          borderBlock: "1px solid var(--line)",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: "1.6rem 2rem",
          }}
        >
          {(page.keyFacts || []).map(([k, v], i) => (
            <Reveal key={i}>
              <span
                className="mono"
                style={{
                  color: "var(--brass-deep)",
                  fontSize: ".68rem",
                  letterSpacing: ".14em",
                  textTransform: "uppercase",
                }}
              >
                {k}
              </span>
              <p
                style={{
                  fontFamily: "var(--display)",
                  fontWeight: 380,
                  fontSize: "1.05rem",
                  margin: ".3rem 0 0",
                  letterSpacing: "-.005em",
                  lineHeight: 1.35,
                }}
              >
                {v}
              </p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* BODY sections */}
      <section className="wrap" style={{ paddingBlock: "var(--section)", maxWidth: "820px" }}>
        {(page.sections || []).map((s, i) => (
          <Reveal
            key={i}
            as="section"
            style={{ marginTop: i === 0 ? 0 : "2.6rem" }}
            data-testid={`region-category-section-${i}`}
          >
            <h2
              className="display"
              style={{
                fontSize: "clamp(1.5rem, 2.4vw, 2rem)",
                margin: "0 0 1.1rem",
                letterSpacing: "-.01em",
                lineHeight: 1.15,
              }}
            >
              {s.h2}
            </h2>
            {(s.body || []).map((para, ix) => (
              <p
                key={ix}
                style={{
                  color: "var(--ink-soft)",
                  fontSize: "1.04rem",
                  lineHeight: 1.75,
                  margin: "0 0 1.1rem",
                  maxWidth: "68ch",
                }}
              >
                {para}
              </p>
            ))}
          </Reveal>
        ))}

        {/* Related services */}
        <Reveal
          as="section"
          style={{ marginTop: "2.6rem" }}
          data-testid="region-category-related-services"
        >
          <h2
            className="display"
            style={{
              fontSize: "clamp(1.5rem, 2.4vw, 2rem)",
              margin: "0 0 1.1rem",
              letterSpacing: "-.01em",
              lineHeight: 1.15,
            }}
          >
            Related services for {page.category} programmes
          </h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              gap: "1rem",
              marginTop: "1rem",
            }}
          >
            {(page.relatedServices || []).map((r, i) => (
              <Link
                key={i}
                to={r.slug}
                data-testid={`region-category-service-${i}`}
                style={{
                  display: "block",
                  padding: "1.2rem 1.3rem",
                  border: "1px solid var(--line)",
                  color: "var(--ink)",
                  textDecoration: "none",
                  background: "var(--paper)",
                  transition: "border-color 200ms",
                }}
              >
                <p
                  className="mono"
                  style={{
                    color: "var(--brass-deep)",
                    fontSize: ".62rem",
                    letterSpacing: ".14em",
                    textTransform: "uppercase",
                    margin: "0 0 .4rem",
                  }}
                >
                  Service
                </p>
                <p
                  style={{
                    fontFamily: "var(--display)",
                    fontSize: "1.05rem",
                    letterSpacing: "-.005em",
                    margin: 0,
                  }}
                >
                  {r.name} →
                </p>
              </Link>
            ))}
          </div>
        </Reveal>

        {/* Related UK cities */}
        {page.relatedCities && page.relatedCities.length > 0 && (
          <Reveal
            as="section"
            style={{ marginTop: "2.6rem" }}
            data-testid="region-category-related-cities"
          >
            <h2
              className="display"
              style={{
                fontSize: "clamp(1.5rem, 2.4vw, 2rem)",
                margin: "0 0 1.1rem",
                letterSpacing: "-.01em",
                lineHeight: 1.15,
              }}
            >
              {country.countryName} city programmes for {page.category}
            </h2>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                gap: "1rem",
                marginTop: "1rem",
              }}
            >
              {page.relatedCities.map((c, i) => (
                <Link
                  key={i}
                  to={c.slug}
                  data-testid={`region-category-city-${i}`}
                  style={{
                    display: "block",
                    padding: "1.2rem 1.3rem",
                    border: "1px solid var(--line)",
                    color: "var(--ink)",
                    textDecoration: "none",
                    background: "var(--paper)",
                  }}
                >
                  <p
                    className="mono"
                    style={{
                      color: "var(--brass-deep)",
                      fontSize: ".62rem",
                      letterSpacing: ".14em",
                      textTransform: "uppercase",
                      margin: "0 0 .4rem",
                    }}
                  >
                    City programme
                  </p>
                  <p
                    style={{
                      fontFamily: "var(--display)",
                      fontSize: "1.05rem",
                      letterSpacing: "-.005em",
                      margin: 0,
                    }}
                  >
                    India sourcing · {c.name} →
                  </p>
                </Link>
              ))}
            </div>
          </Reveal>
        )}

        {/* Case study link */}
        {page.caseStudy && (
          <Reveal
            as="section"
            style={{ marginTop: "2.6rem" }}
            data-testid="region-category-case-study"
          >
            <Link
              to={page.caseStudy.slug}
              style={{
                display: "block",
                padding: "1.6rem 1.8rem",
                border: "1px solid var(--brass-deep)",
                color: "var(--ink)",
                textDecoration: "none",
                background: "var(--paper)",
              }}
            >
              <p
                className="mono"
                style={{
                  color: "var(--brass-deep)",
                  fontSize: ".68rem",
                  letterSpacing: ".14em",
                  textTransform: "uppercase",
                  margin: "0 0 .4rem",
                }}
              >
                Case study
              </p>
              <p
                style={{
                  fontFamily: "var(--display)",
                  fontSize: "1.2rem",
                  letterSpacing: "-.005em",
                  margin: 0,
                }}
              >
                Read: {page.caseStudy.name} →
              </p>
            </Link>
          </Reveal>
        )}
      </section>

      {/* FAQ */}
      <section
        className="wrap"
        data-testid="region-category-faq"
        style={{ paddingBlock: "var(--section)", maxWidth: "820px" }}
      >
        <h2
          className="display"
          style={{
            fontSize: "clamp(1.5rem, 2.4vw, 2rem)",
            margin: "0 0 1.4rem",
            letterSpacing: "-.01em",
          }}
        >
          Frequently asked questions — {country.countryName} {page.category} programmes
        </h2>
        {(page.faqs || []).map(([q, a], i) => (
          <details
            key={i}
            data-testid={`region-category-faq-${i}`}
            style={{
              borderTop: i === 0 ? "1px solid var(--line)" : "none",
              borderBottom: "1px solid var(--line)",
              padding: "1.2rem 0",
            }}
          >
            <summary
              style={{
                cursor: "pointer",
                fontFamily: "var(--display)",
                fontSize: "1.1rem",
                letterSpacing: "-.005em",
                listStyle: "none",
              }}
            >
              {q}
            </summary>
            <p
              style={{
                color: "var(--ink-soft)",
                fontSize: "1rem",
                lineHeight: 1.7,
                margin: ".8rem 0 0",
                maxWidth: "68ch",
              }}
            >
              {a}
            </p>
          </details>
        ))}
      </section>

      {/* Sibling UK category cross-links */}
      <section
        className="wrap"
        data-testid="region-category-siblings"
        style={{ paddingBlock: "var(--section)", borderTop: "1px solid var(--line)" }}
      >
        <p
          className="mono"
          style={{
            color: "var(--brass-deep)",
            fontSize: ".68rem",
            letterSpacing: ".14em",
            textTransform: "uppercase",
            margin: "0 0 .8rem",
          }}
        >
          Other {country.countryName} category programmes
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
            gap: "1rem",
          }}
        >
          {siblings.map((s, i) => (
            <Link
              key={i}
              to={s.slug}
              data-testid={`region-category-sibling-${s.categorySlug}`}
              style={{
                display: "block",
                padding: "1.1rem 1.2rem",
                border: "1px solid var(--line)",
                color: "var(--ink)",
                textDecoration: "none",
                background: "var(--paper)",
              }}
            >
              <p
                className="mono"
                style={{
                  color: "var(--brass-deep)",
                  fontSize: ".62rem",
                  letterSpacing: ".14em",
                  textTransform: "uppercase",
                  margin: "0 0 .3rem",
                }}
              >
                {country.countryName} programme
              </p>
              <p
                style={{
                  fontFamily: "var(--display)",
                  fontSize: "1.05rem",
                  letterSpacing: "-.005em",
                  margin: 0,
                }}
              >
                {s.category} →
              </p>
            </Link>
          ))}
        </div>
      </section>

      {/* Final enquiry CTA */}
      <section
        className="wrap"
        style={{ paddingBlock: "var(--section)", borderTop: "1px solid var(--line)" }}
        data-testid="region-category-enquiry"
      >
        <Reveal as="p" className="eyebrow">
          Start an enquiry
        </Reveal>
        <Reveal as="h2" className="display" style={{ margin: "1rem 0 2rem" }}>
          {country.countryName} {page.category} sourcing brief — talk to Asia Sourcing.
        </Reveal>
        <Reveal
          as="div"
          style={{ display: "flex", flexWrap: "wrap", gap: "1rem", marginTop: "1rem" }}
        >
          <button
            type="button"
            className="btn btn-fill btn-lg btn-glow"
            onClick={open}
            data-testid="region-category-cta-enquiry"
          >
            Start Your Project →
          </button>
          <Link to="/contact" className="btn btn-ghost" data-testid="region-category-cta-contact">
            Send an enquiry
          </Link>
        </Reveal>
      </section>
    </article>
  );
};

export default RegionCategory;
